import { memo, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Menu } from 'antd';

import { BarChartCard, BarChartSubtitle } from 'componets/ui';
import { DropDownComponent } from 'componets/common';

import { getIncome, getSecondaryMarketIncome } from 'features/statement/actions';

import i18n from 'utils/i18n';

import { useAppSelector, useTranslate } from 'hooks';

import styles from 'pages/Account/Overview/ProfitStatus/index.module.scss';

const BARS = [
	{ dataKey: 'income', name: 'incomeText', fill: '#65ABFF' },
	{ dataKey: 'tax', name: 'taxText', fill: '#FB827A' },
];

export const INCOME_TYPES = [
	{ label: i18n.t('primaryMarketIncomeText'), value: 'primary' },
	{ label: i18n.t('secondaryMarketIncomeText'), value: 'secondary' },
];

export const Income = memo(() => {
	const income = useAppSelector((state) => state.statement.income);

	const [type, setType] = useState('primary');
	const isPrimary = type === 'primary';

	const { incomeText, taxText, primaryMarketText, secondaryMarketText } = useTranslate([
		'incomeText',
		'taxText',
		'primaryMarketText',
		'secondaryMarketText',
	]);

	const { items } = income;

	const { total, data } = useMemo(() => {
		let total = 0;
		const data = items.map((item) => {
			const income = Math.abs(Number(item.income));
			const tax = Math.abs(Number(item.tax));
			total += Number(item.income) + Number(item.tax);

			return {
				...item,
				income,
				tax,
				negativeTax: Number(item.tax) < 0,
				negativeIncome: Number(item.income) < 0,
			};
		});

		return { total, data };
	}, [items]);

	const hasNegativeValues = useMemo(() => data?.some((item) => item.negativeIncome), [data]);
	const areAllNegativeValues = useMemo(
		() => data.every((item) => item.negativeIncome || item.income === 0),
		[data],
	);

	const className = useMemo(
		() =>
			areAllNegativeValues && total !== 0
				? styles.yellow
				: hasNegativeValues
				? styles.gradient
				: '',
		[areAllNegativeValues, hasNegativeValues, total],
	);

	return (
		<BarChartCard
			subtitle={
				<div className={styles.root}>
					<BarChartSubtitle
						first={income.income}
						second={income.tax}
						titleOne={incomeText}
						titleTwo={taxText}
						className={className}
					/>

					<div className="chartType">
						<DropDownComponent
							placement="bottom"
							dropdownRender={() => (
								<Menu className={styles.dropdownWrapper}>
									{INCOME_TYPES.map((item) => {
										return (
											<Menu.Item
												className={classNames(styles.documentButton, {
													[styles.active]: item.value === type,
												})}
												key={item.value}
												onClick={() => {
													setType(item.value);
												}}
											>
												{item.label}
											</Menu.Item>
										);
									})}
								</Menu>
							)}
							label={isPrimary ? primaryMarketText : secondaryMarketText}
						/>
					</div>
				</div>
			}
			data={data}
			getData={isPrimary ? getIncome : getSecondaryMarketIncome}
			title={incomeText}
			total={total}
			bars={BARS}
		/>
	);
});
